import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { find } from 'ramda';
import Form from '~~elements/Form';
import Input from '~~elements/Input';
import UploadField from '~~elements/UploadField';
import Button from '~~elements/Button';
import Select from '~~elements/Select';
import { useRootState } from '../../../../useRoot';
import { parseTeamData } from '../service';
import { AvatarDisplay, ImageWrapper, ColumnWrapper } from '../styled';
import UTRMultilingual from '../../Multilingual';
import RMultilingual from '../../../Tournament/Registry/Multilingual';
import Column from './Column';
import { StyledDiv as ColumnStyledDiv } from './Column';

const StyledDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .description {
    margin: 2rem 0;
    white-space: pre-line;
    letter-spacing: 2px;
    fint-size: 0.9rem;
    text-align: start;
  }

  input:disabled {
    opacity: 0.8;
  }
`;

const TeamSection = ({ teamInRegistry, registry, onChange, apiInProgress, nonOrgMode = true }) => {
  const { permissionList } = useRootState();

  const [teamsInControl, setTeamsInControl] = useState([]);
  const [currentTeamData, setCurrentTeamData] = useState(parseTeamData(teamInRegistry, registry));

  useEffect(() => {
    if (!permissionList) {
      setTeamsInControl([]);
      return;
    }

    const inControlTeams = [];
    permissionList.teams.forEach((team) => {
      if (!['ADMIN', 'EDITOR'].includes(team.permission)) {
        return;
      }

      inControlTeams.push(team.info);
    });
    setTeamsInControl(inControlTeams);
  }, [permissionList]);

  useEffect(() => {
    setCurrentTeamData(parseTeamData(teamInRegistry, registry));
  }, [registry, teamInRegistry]);

  useEffect(() => {
    const originTeamData = JSON.stringify(parseTeamData(teamInRegistry, registry));
    if (originTeamData === JSON.stringify(currentTeamData)) {
      onChange(null);
      return;
    }

    onChange(currentTeamData);
    return;
  }, [currentTeamData, onChange, registry, teamInRegistry]);

  const handleBasicInputOnChange = (e) => {
    setCurrentTeamData({
      ...currentTeamData,
      [e.target.name]: e.target.value,
    });
  };

  const handleConnectedTeamOnChange = (e) => {
    const targetTeam = find((t) => t.uniqid === e.target.value, teamsInControl);
    if (!targetTeam) {
      setCurrentTeamData({
        ...currentTeamData,
        connected_team: '',
        name: '',
        city: '',
        icon_path: '',
        icon_url: '',
      });
      return;
    }

    setCurrentTeamData({
      ...currentTeamData,
      connected_team: targetTeam.uniqid,
      name: targetTeam.name,
      city: targetTeam.city,
      icon_path: targetTeam.icon_path,
      icon_url: targetTeam.icon_url,
    });
  };

  const handleIconUpdate = (data) => {
    setCurrentTeamData({
      ...currentTeamData,
      icon_path: data.path,
      icon_url: data.url,
    });
  };

  const handleRemoveIcon = () => {
    setCurrentTeamData({
      ...currentTeamData,
      icon_path: '',
      icon_url: '',
    });
  };

  const handleColumnOnChange = (columnIndex) => (data) => {
    currentTeamData.columns[columnIndex] = {
      ...currentTeamData.columns[columnIndex],
      value: data.value,
      url: data.url,
    };

    const removeImagePaths = [...currentTeamData.remove_image_paths];
    if (data.removedImage) {
      removeImagePaths.push(data.removedImage);
    }

    setCurrentTeamData({
      ...currentTeamData,
      columns: [...currentTeamData.columns],
      remove_image_paths: [...removeImagePaths],
    });
  };

  return (
    <StyledDiv>
      {nonOrgMode && (
        <div className="description">
          {`${RMultilingual(`TEAM_DESCRIPTION`)}：`}
          {`${!registry.team_description ? UTRMultilingual(`COMMON.NONE`) : registry.team_description}`}
        </div>
      )}
      <Form onSubmit={(e) => e.preventDefault()}>
        <div className="box">
          {nonOrgMode && (
            <Fragment>
              <label>{UTRMultilingual(`CUSTOM.TABLE.HISTORY_CONNECTION`)}</label>
              <div className="input-field">
                <Select
                  name="connected_team"
                  value={currentTeamData.connected_team}
                  onChange={handleConnectedTeamOnChange}
                  disabled={apiInProgress}
                >
                  {teamsInControl.map((teamInControl, index) => (
                    <option key={index} value={teamInControl.uniqid}>
                      {teamInControl.name}
                    </option>
                  ))}
                  <option value="">{UTRMultilingual(`COMMON.NONE`)}</option>
                </Select>
              </div>
            </Fragment>
          )}
          {!!currentTeamData.connected_team && nonOrgMode && (
            <div className="description">{UTRMultilingual(`CUSTOM.HISTORY_CONNECTION_CITY_ICON_HINT`)}</div>
          )}
          <div className="input-field" />
          <div className="input-field">
            <div className="input-list">
              <ColumnStyledDiv>
                <label>
                  {RMultilingual(`DEFAULT_COLUMN.TEAM.NAME.NAME`)}
                  <i>*</i>
                </label>
                <Input
                  name="name"
                  value={currentTeamData.name}
                  onChange={handleBasicInputOnChange}
                  disabled={apiInProgress}
                />
              </ColumnStyledDiv>
              <ColumnStyledDiv>
                <label>
                  {RMultilingual(`DEFAULT_COLUMN.TEAM.CITY.NAME`)}
                  <i>*</i>
                </label>
                <Input
                  name="city"
                  value={currentTeamData.city}
                  onChange={handleBasicInputOnChange}
                  disabled={apiInProgress || !!currentTeamData.connected_team}
                />
              </ColumnStyledDiv>
              <ColumnStyledDiv>
                <label>
                  {RMultilingual(`DEFAULT_COLUMN.TEAM.ICON.NAME`)}
                  <i>*</i>
                </label>
                {apiInProgress || !!currentTeamData.connected_team ? (
                  <ImageWrapper avatarRatio={1}>
                    <AvatarDisplay
                      url={
                        !!currentTeamData.connected_team
                          ? teamInRegistry.connected_team.icon_url
                          : currentTeamData.icon_url
                      }
                    />
                  </ImageWrapper>
                ) : (
                  <ImageWrapper avatarRatio={1}>
                    {currentTeamData.icon_path ? (
                      <Fragment>
                        <AvatarDisplay url={currentTeamData.icon_url} />
                        <UploadField
                          styleType="box"
                          url="/api/media"
                          customBtn={(onClick) => (
                            <Button width="unset" color="white" onClick={onClick}>
                              {UTRMultilingual(`FORM.REPLACE`)}
                            </Button>
                          )}
                          onChange={handleIconUpdate}
                        />
                        <Button width="unset" color="red" onClick={handleRemoveIcon} mini>
                          {UTRMultilingual(`COMMON.REMOVE`)}
                        </Button>
                      </Fragment>
                    ) : (
                      <UploadField
                        styleType="box"
                        url="/api/media"
                        customBtn={(onClick) => (
                          <Button width="unset" color="blue" onClick={onClick}>
                            {UTRMultilingual(`FORM.UPLOAD`)}
                          </Button>
                        )}
                        onChange={handleIconUpdate}
                      />
                    )}
                  </ImageWrapper>
                )}
              </ColumnStyledDiv>
              <ColumnStyledDiv>
                <label>
                  {RMultilingual(`DEFAULT_COLUMN.TEAM.CONTACT_PERSON.NAME`)}
                  <i>*</i>
                </label>
                <Input
                  name="contact_person"
                  value={currentTeamData.contact_person}
                  onChange={handleBasicInputOnChange}
                  disabled={apiInProgress}
                />
              </ColumnStyledDiv>
              <ColumnStyledDiv>
                <label>
                  {RMultilingual(`DEFAULT_COLUMN.TEAM.CONTACT_PHONE.NAME`)}
                  <i>*</i>
                </label>
                <Input
                  name="contact_phone"
                  value={currentTeamData.contact_phone}
                  onChange={handleBasicInputOnChange}
                  disabled={apiInProgress}
                />
              </ColumnStyledDiv>
            </div>
          </div>
          <ColumnWrapper>
            {currentTeamData.columns.map((column, index) => (
              <Column
                key={index}
                column={column}
                onChange={handleColumnOnChange(index)}
                apiInProgress={apiInProgress}
              />
            ))}
          </ColumnWrapper>
        </div>
      </Form>
    </StyledDiv>
  );
};

export default TeamSection;
